import { useMemo } from 'react'
import floor from 'lodash/floor'
import { IShipmentCommissionTierBands, Maybe, IBidRateCoachGrades } from 'types/graphqlTypes'
import { calculateRateCoachGrade } from '../../RateCoachGauge'
import { useShipmentRateInfoQuery } from '../graphql/ShipmentRateInfo'

export interface IUseShipmentRatesState {
  commissionTierBands: Maybe<IShipmentCommissionTierBands>
  loading: boolean
  listRate: Maybe<number>
  percentToMarket: Maybe<number>
  rateCoachGrade: Maybe<IBidRateCoachGrades>
  repTarget: Maybe<number>
  shipperRate: Maybe<number>
}

export interface IUseShipmentRatesProps {
  shipmentId: Maybe<string>
  rate?: Maybe<number>
}

export const useShipmentRates = ({
  rate,
  shipmentId,
}: IUseShipmentRatesProps): IUseShipmentRatesState => {
  const { data, loading } = useShipmentRateInfoQuery({
    variables: {
      shipmentId: shipmentId || '',
    },
    skip: !shipmentId,
  })
  const { shipment } = data || {}
  const {
    cmTarget,
    commissionTierBands,
    datMarketRate,
    listRate,
    optionGradeTiers,
    shipperTotalRate,
  } = shipment || {}
  const { rate: dateRateOb } = datMarketRate || {}
  const { amount: datRate } = dateRateOb || {}

  const roundedCommissionTierBands: Maybe<IShipmentCommissionTierBands> = useMemo(() => {
    const { tier1Max, tier2Max, tier3Max } = commissionTierBands ?? {}

    if (!tier1Max || !tier2Max || !tier3Max) {
      return null
    }

    return {
      tier1Max: floor(tier1Max, 2),
      tier2Max: floor(tier2Max, 2),
      tier3Max: floor(tier3Max, 2),
    }
  }, [commissionTierBands])

  const rateCoachGrade = useMemo(() => calculateRateCoachGrade({ optionGradeTiers, rate }), [
    optionGradeTiers,
    rate,
  ])

  const percentToMarket = useMemo(() => {
    if (rate && datRate) {
      return rate / datRate
    }
    return null
  }, [rate, datRate])

  return {
    commissionTierBands: roundedCommissionTierBands,
    loading,
    listRate,
    repTarget: cmTarget,
    shipperRate: shipperTotalRate,
    percentToMarket,
    rateCoachGrade,
  }
}
